/**
 * Query Module Types
 *
 * Mirrors the srg-backend Query Module API (entities, requests, responses).
 */

export type QueryType = 'personal' | 'global';

export type QueryEntityKey = 'supplier' | 'contract' | 'assessment' | 'meeting' | 'division' | 'contact';

export type QueryFieldType = 'text' | 'number' | 'date' | 'boolean' | 'enum';

export type QueryFilterOperator =
  | 'equals'
  | 'notEqual'
  | 'contains'
  | 'notContains'
  | 'isEmpty'
  | 'isNotEmpty'
  | 'greaterThan'
  | 'lessThan'
  | 'dateRange';

export type QueryCellValue = string | number | boolean | null;

export interface QueryColumn {
  entity: string;
  field: string;
  order: number;
}

export interface QueryFilter {
  entity: string;
  field: string;
  operator: QueryFilterOperator;
  value?: QueryCellValue;
  valueTo?: QueryCellValue;
}

/** A saved query definition (dates serialized as ISO strings). */
export interface Query {
  id: number;
  name: string;
  type: QueryType;
  entities: string[];
  columns: QueryColumn[];
  filters: QueryFilter[];
  createdBy: number;
  createdAt: string;
  updatedAt: string;
  /** Raw SQL of the most recent execution; null until first run. */
  lastExecutedSql: string | null;
}

export interface QueryListItem {
  id: number;
  name: string;
  type: QueryType;
  createdBy: number;
  creatorName: string;
  createdAt: string;
  updatedAt: string;
}

/* ----- Dynamic schema (from Prisma datamodel) ----- */

export interface QueryField {
  key: string;
  label: string;
  type: QueryFieldType;
  /** Allowed values for `enum` fields — drives the builder dropdown. */
  options?: string[];
  /**
   * True for cross-relation ("derived") fields surfaced from a related entity
   * (e.g. an Assessment's supplier name). Plain scalar fields omit this (#919).
   */
  derived?: boolean;
  /** Whether the field can be filtered on. Absent ⇒ true (plain scalars). */
  filterable?: boolean;
  /** Whether the field can be sorted on. Absent ⇒ true (plain scalars). */
  sortable?: boolean;
}

export interface QueryEntity {
  key: string;
  label: string;
  fields: QueryField[];
}

export interface QueryRelationship {
  from: string;
  to: string;
}

export interface QuerySchemaResponse {
  entities: QueryEntity[];
  relationships: QueryRelationship[];
}

/* ----- Requests / responses ----- */

export interface CreateQueryRequest {
  name: string;
  type: QueryType;
  entities: string[];
  columns: QueryColumn[];
  filters: QueryFilter[];
}

export type UpdateQueryRequest = CreateQueryRequest;

/** Type-filter tabs for the query list (US-02). */
export type QueryListTypeFilter = 'all' | 'personal' | 'global';

/** Sortable list columns supported server-side (US-02). */
export type QueryListSortField = 'name' | 'createdAt' | 'updatedAt';

/** Per-type match counts for the list filter tabs (US-02). */
export interface QueryListCounts {
  all: number;
  personal: number;
  global: number;
}

export interface ListQueriesParams {
  type?: QueryListTypeFilter;
  search?: string;
  sortBy?: QueryListSortField;
  sortDir?: 'asc' | 'desc';
  page?: number;
  pageSize?: number;
}

export interface QueryListResponse {
  count: number;
  data: QueryListItem[];
  page: number;
  pageSize: number;
  counts: QueryListCounts;
}

export interface MutateQueryResponse {
  message: string;
  data: Query;
}

export interface DeleteQueryResponse {
  success: true;
  message: string;
}

export interface QuerySort {
  field: string;
  direction: 'asc' | 'desc';
}

export interface ExecuteQueryRequest {
  page?: number;
  pageSize?: number;
  adHocFilters?: QueryFilter[];
  sort?: QuerySort;
}

/**
 * Body for previewing an unsaved query (POST /api/queries/preview, #863).
 * Carries the full definition plus per-execution pagination/sort; no name or
 * type because a preview is never persisted.
 */
export interface PreviewQueryRequest {
  entities: string[];
  columns: QueryColumn[];
  filters: QueryFilter[];
  page?: number;
  pageSize?: number;
  sort?: QuerySort;
}

/** Preview results — like QueryResultsResponse but without the query identity. */
export interface QueryPreviewResponse {
  columns: string[];
  rows: QueryResultRow[];
  totalCount: number;
  page: number;
  pageSize: number;
  sql: string | null;
}

export type QueryResultRow = Record<string, QueryCellValue>;

export interface QueryResultsResponse {
  queryId: number;
  queryName: string;
  columns: string[];
  rows: QueryResultRow[];
  totalCount: number;
  page: number;
  pageSize: number;
  sql: string | null;
  sqlPersistError: string | null;
}

/** Allowed page sizes for list and result pagination (US-02, US-08). */
export const QUERY_PAGE_SIZES = [25, 50, 100] as const;
export const DEFAULT_QUERY_PAGE_SIZE = 25;

/** Filter operators available per field type, for the builder UI. */
export const OPERATORS_BY_FIELD_TYPE: Record<QueryFieldType, QueryFilterOperator[]> = {
  text: ['equals', 'notEqual', 'contains', 'notContains', 'isEmpty', 'isNotEmpty'],
  number: ['equals', 'notEqual', 'greaterThan', 'lessThan', 'isEmpty', 'isNotEmpty'],
  date: ['equals', 'dateRange', 'greaterThan', 'lessThan', 'isEmpty', 'isNotEmpty'],
  boolean: ['equals'],
  enum: ['equals', 'notEqual', 'isEmpty', 'isNotEmpty']
};

/** Human-readable labels for a query's type (list badge, details header). */
export const QUERY_TYPE_LABELS: Record<QueryType, string> = {
  personal: 'Personal',
  global: 'Global'
};

/** Human-readable labels for filter operators (builder + results ad-hoc filters). */
export const OPERATOR_LABELS: Record<QueryFilterOperator, string> = {
  equals: 'Equals',
  notEqual: 'Not equal',
  contains: 'Contains',
  notContains: 'Does not contain',
  isEmpty: 'Is empty',
  isNotEmpty: 'Is not empty',
  greaterThan: 'Greater than',
  lessThan: 'Less than',
  dateRange: 'Date range'
};
